import type { ProductSizeOption } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { type DestinationFieldErrors, DestinationSizeRows } from '@/entities/quote';
import { Button, FormField, ShippingAddressFields } from '@/shared/ui';
import type { MemberDestinationValues } from '../model/use-submit-quote-request';

export interface MemberDestinationCardProps {
  /** 1부터 — 카드 제목 "배송지 N" 과 삭제 버튼 이름에 쓴다. */
  readonly order: number;
  readonly value: MemberDestinationValues;
  readonly errors: DestinationFieldErrors;
  readonly canRemove: boolean;
  readonly disabled?: boolean;
  readonly sizeOptions?: readonly ProductSizeOption[];
  readonly showQuantity?: boolean;
  readonly showSizeRows?: boolean;
  /** 바뀐 칸만 넘긴다 — 목록 훅이 이전 값과 병합한다. */
  readonly onChange: (patch: Partial<MemberDestinationValues>) => void;
  readonly onRemove: () => void;
}

/**
 * 회원 견적 요청 배송지 카드(spec §6-1) — 배송지 다섯 칸(`ShippingAddressFields`) + 이 배송지로 받을 수량 + [삭제](2장 이상일 때).
 * 카드 제목 "배송지 N" 이 묶음(group) 이름이다.
 */
export const MemberDestinationCard = ({
  order,
  value,
  errors,
  canRemove,
  disabled = false,
  sizeOptions = [],
  showQuantity = sizeOptions.length === 0,
  showSizeRows = false,
  onChange,
  onRemove,
}: MemberDestinationCardProps): ReactElement => (
  // 제목(legend)을 띄워(float) 카드 안 첫 줄에 두고, [삭제]는 같은 줄 오른쪽 끝에 둔다.
  <fieldset disabled={disabled} className="relative min-w-0 border-t border-foreground/15 pt-5">
    <legend className="float-left text-base leading-9 font-semibold">배송지 {order}</legend>
    {canRemove ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="absolute top-6 right-0"
        aria-label={`배송지 ${order} 삭제`}
        onClick={onRemove}
      >
        삭제
      </Button>
    ) : null}

    <div className="clear-both flex flex-col gap-5 pt-4">
      <ShippingAddressFields
        value={value}
        errors={{
          recipientName: errors.recipientName,
          recipientPhone: errors.recipientPhone,
          postalCode: errors.postalCode,
          address1: errors.address1,
          address2: errors.address2,
        }}
        onChange={onChange}
      />

      {sizeOptions.length > 0 ? (
        showSizeRows ? (
          <DestinationSizeRows
            options={sizeOptions}
            rows={value.sizes}
            error={errors.sizes}
            onChange={(sizes) => onChange({ sizes })}
          />
        ) : null
      ) : showQuantity ? (
        <FormField
          label="수량"
          inputMode="numeric"
          autoComplete="off"
          isRequired
          required
          className="h-11 tabular-nums"
          containerClassName="sm:max-w-xs"
          value={value.quantity}
          error={errors.quantity}
          onChange={(event) => onChange({ quantity: event.target.value })}
        />
      ) : null}
    </div>
  </fieldset>
);
